import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import StairsIcon from '@mui/icons-material/Stairs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useListingHeaderLogic } from '../hooks/useListingHeaderLogic';
import { PropertyField } from '../../../../system/field';
import { useHeaderObserver } from '../hooks/useHeaderObserver';

const TOP_OFFSET = 60;

interface MobileHeaderProps {
  logic: ReturnType<typeof useListingHeaderLogic>;
  handleGroupClick: (
    groupName: string,
    fieldKey?: string,
    isMobile?: boolean,
  ) => void;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  logic,
  handleGroupClick,
}) => {
  const { ref: headerRef, headerHeight } = useHeaderObserver();
  const {
    address,
    formattedStatus,
    formattedPrice,
    formattedExpirationDate,
    beds,
    baths,
    stories,
    handleNavigateBack,
    latitude,
    longitude,
  } = logic;

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [shouldApplyOffset, setShouldApplyOffset] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (currentScroll > 0) {
        setIsHeaderVisible(currentScroll <= lastScrollY);
      } else {
        // If you "overscroll" to -y, lastScrollY will be >= currentScroll as the browser adjusts back to 0 (top of screen)
        // we still need the header to be visible in this case
        setIsHeaderVisible(true);
      }
      setLastScrollY(currentScroll);

      // Only apply offset when we're at or beyond the very top
      setShouldApplyOffset(currentScroll <= 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <>
      {/* Spacer includes the offset only when at the top */}
      <Box
        sx={{
          height: headerHeight,
          width: '100%',
          transition: 'height 0.3s ease',
        }}
      />

      <Box
        ref={headerRef}
        sx={{
          display: 'flex',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          backgroundColor: 'white',
          borderBottom: '1px solid',
          borderColor: 'grey.300',
          position: 'fixed',
          top: shouldApplyOffset ? TOP_OFFSET : 0,
          left: 0,
          right: 0,
          transform: `translateY(${isHeaderVisible ? '0' : `-${headerHeight + (shouldApplyOffset ? TOP_OFFSET : 0)}px`})`,
          transition: (theme) =>
            theme.transitions.create(['transform', 'top'], {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeInOut,
            }),
          zIndex: 1000,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton sx={{ mr: 2 }} onClick={() => handleNavigateBack()}>
            <ArrowBackIcon sx={{ color: 'primary.main' }} />
          </IconButton>
          <Box>
            <Typography variant="h6">{address}</Typography>
            <Box display="flex" gap={1} mb={1}>
              <Typography
                variant="body2"
                display="flex"
                alignItems="center"
                sx={{
                  color: 'theme.palette.grey[500]',
                }}
                fontWeight={700}
              >
                Lat:
              </Typography>
              <Typography
                variant="body2"
                display="flex"
                alignItems="center"
                sx={{
                  color: 'theme.palette.grey[500]',
                }}
              >
                {latitude}
              </Typography>
              <Typography
                variant="body2"
                display="flex"
                alignItems="center"
                sx={{
                  color: 'theme.palette.grey[500]',
                }}
                fontWeight={700}
              >
                Long:
              </Typography>
              <Typography
                variant="body2"
                display="flex"
                alignItems="center"
                sx={{
                  color: 'theme.palette.grey[500]',
                }}
              >
                {longitude}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                gap: 2,
              }}
            >
              <Typography variant="body2">
                Price:{' '}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    textDecoration: 'underline',
                    color: 'theme.palette.secondary.medium',
                  }}
                  onClick={() =>
                    handleGroupClick(
                      'Listing Details', // where can I get this from?
                      PropertyField.LIST_PRICE,
                    )
                  }
                >
                  {formattedPrice}
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Expires:{' '}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    textDecoration: 'underline',
                    color: 'theme.palette.secondary.medium',
                  }}
                  onClick={() =>
                    handleGroupClick(
                      'Listing Agreement', // where can I get this from?
                      PropertyField.EXPIRATION_DATE,
                    )
                  }
                >
                  {formattedExpirationDate}
                </Typography>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Typography
                variant="body2"
                display="flex"
                alignItems="center"
                sx={{
                  color: 'theme.palette.grey[500]',
                }}
              >
                <BedIcon fontSize="small" /> {beds}
              </Typography>
              <Typography
                variant="body2"
                display="flex"
                alignItems="center"
                sx={{
                  color: 'theme.palette.grey[500]',
                }}
              >
                <BathtubIcon fontSize="small" /> {baths}
              </Typography>
              <Typography
                variant="body2"
                display="flex"
                alignItems="center"
                sx={{
                  color: 'theme.palette.grey[500]',
                }}
              >
                <StairsIcon fontSize="small" /> {stories}
              </Typography>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{
                  textDecoration: 'underline',
                  color: 'theme.palette.secondary.medium',
                }}
                onClick={() =>
                  handleGroupClick(
                    'Listing Details', // where can I get this from?
                    PropertyField.MLS_STATUS,
                  )
                }
              >
                {formattedStatus}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MobileHeader;
